// Vehicle Scheduler page — wires the store, the 90-day window (mirrors
// TourTimeline), the grid, the cell popover, the leg editor, and the drag
// controller. Edits are IMMEDIATE (no Save bar): assigning writes event_vehicle,
// dragging a band edge PATCHes the event's ship date. Producers edit; operations
// and managers see it read-only.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import useUserStore from '../stores/useUserStore';
import useVehicleScheduleStore, { RENTAL_VEHICLE_ID } from '../stores/useVehicleScheduleStore';
import { useToast } from '../components/Toast/ToastContext';
import { enumerateDays } from '../utils/date-utils';
import VehicleScheduleGrid, { BandMovePayload } from './VehicleScheduleGrid';
import VehicleCellPopover from './VehicleCellPopover';
import LegEditorModal from './LegEditorModal';
import TourProposalModal from './TourProposalModal';
import BandContextMenu, { MenuItem } from './BandContextMenu';
import VehicleDragController from './VehicleDragController';
import { BandContextMenuArgs } from './VehicleBand';
import { EventHoverCardPortal, HoverCardState } from '../Events/EventHoverCard';
import DayModeToggle from '../components/DayModeToggle';
import { VehicleScheduleEvent } from './types';
import { bandExtent, deriveRentalLanes, stackedWith } from './band-runs';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import { Appear } from '../components/ui/Appear';
import { FadeIn } from '../components/ui/FadeIn';
import LogoSpinner from '../components/ui/LogoSpinner';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';
import { DAY_W } from './grid-constants';

const WINDOW_DAYS = 90;
const ymd = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');

const VehicleSchedulePage: React.FC = () => {
  const user = useUserStore((s: any) => s.user);
  const canAccess = !!(
    user?.isadmin || user?.can_admin || user?.can_operations ||
    user?.can_producer || user?.can_manager
  );
  // Writes are producer-only (admin bypasses); operations/manager are view-only.
  const canEdit = !!(user?.isadmin || user?.can_admin || user?.can_producer);

  const vehicles = useVehicleScheduleStore(s => s.vehicles);
  const events = useVehicleScheduleStore(s => s.events);
  const assignments = useVehicleScheduleStore(s => s.assignments);
  const crew = useVehicleScheduleStore(s => s.crew);
  const tours = useVehicleScheduleStore(s => s.tours);
  const unavailability = useVehicleScheduleStore(s => s.unavailability);
  const loading = useVehicleScheduleStore(s => s.loading);
  const error = useVehicleScheduleStore(s => s.error);
  const fetchWindow = useVehicleScheduleStore(s => s.fetchWindow);
  const assignVehicle = useVehicleScheduleStore(s => s.assignVehicle);
  const unassignVehicle = useVehicleScheduleStore(s => s.unassignVehicle);
  const moveAssignment = useVehicleScheduleStore(s => s.moveAssignment);
  const setTransportArranged = useVehicleScheduleStore(s => s.setTransportArranged);
  const setTransportMode = useVehicleScheduleStore(s => s.setTransportMode);
  const splitTour = useVehicleScheduleStore(s => s.splitTour);
  const createTourFromEvents = useVehicleScheduleStore(s => s.createTourFromEvents);
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();

  const [offset, setOffset] = useState(0); // months from current month
  const scrollRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);

  // Deep-link focus: a "Manage transport" link from an event/tour lands here with
  // ?focusEvent=&focusDate=. We park the window a little before that date (so the
  // band's lead-in is visible) and scroll to it once the payload loads. Parsed
  // once on mount.
  const focusParams = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return { event: p.get('focusEvent'), date: p.get('focusDate') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [focusStart, setFocusStart] = useState<string | null>(
    () => (focusParams.date ? ymd(dayjs.utc(focusParams.date).subtract(10, 'day')) : null)
  );

  // Window: today on the default view, the 1st of the month once paged; a hard
  // WINDOW_DAYS span — horizontal scroll covers the overflow.
  const windowStart = useMemo(
    () => focusStart ?? (offset === 0 ? ymd(dayjs.utc()) : ymd(dayjs.utc().startOf('month').add(offset, 'month'))),
    [offset, focusStart]
  );
  const windowEnd = useMemo(() => ymd(dayjs.utc(windowStart).add(WINDOW_DAYS, 'day')), [windowStart]);
  const days = useMemo(() => enumerateDays(windowStart, windowEnd), [windowStart, windowEnd]);
  const todayIdx = useMemo(() => {
    const t = ymd(dayjs.utc());
    const i = days.indexOf(t);
    return i >= 0 ? i : undefined;
  }, [days]);

  useEffect(() => {
    if (canAccess) fetchWindow(windowStart, windowEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, windowStart, windowEnd]);

  useEffect(() => {
    // Wait until the bands are actually rendered (events loaded) so both the
    // scroll target and the focus-pulse query have something to find.
    if (loading || events.length === 0 || didInitialScroll.current) return;
    didInitialScroll.current = true;
    // Scroll to the deep-link focus date if present, else to today.
    const targetIdx = focusParams.date ? days.indexOf(focusParams.date) : todayIdx;
    if (targetIdx !== undefined && targetIdx >= 0 && scrollRef.current) {
      scrollRef.current.scrollLeft = Math.max(0, targetIdx * DAY_W - scrollRef.current.clientWidth / 3);
    }
    // Briefly pulse the focused event's band so it stands out among the lanes
    // (next frame, after the bands have painted).
    if (focusParams.event) {
      requestAnimationFrame(() => {
        document.querySelectorAll(`[data-event-id="${focusParams.event}"]`).forEach(el => {
          el.classList.add('sched-focus-pulse');
          setTimeout(() => el.classList.remove('sched-focus-pulse'), 2700);
        });
      });
    }
  }, [loading, events, todayIdx, days, focusParams]);

  // Events whose SHOW span covers a given day — the assignable pool per cell.
  const eventsPerDay = useMemo(() => {
    const map = new Map<string, typeof events>();
    for (const e of events) {
      if (e.status === 'cancelled') continue;
      const end = e.end_date || e.event_date;
      for (const ds of enumerateDays(e.event_date, end)) {
        const arr = map.get(ds) || [];
        arr.push(e);
        map.set(ds, arr);
      }
    }
    return map;
  }, [events]);

  const eventById = useMemo(() => {
    const m = new Map<string, typeof events[number]>();
    events.forEach(e => m.set(e.id, e));
    return m;
  }, [events]);

  // The Shipping / Rentals bands. Membership is the deliberate transport_arranged
  // flag, NOT transport_mode (which defaults to 'shipping' on every untouched show).
  //
  // An owned-vehicle assignment does NOT exclude a show from here. A truck and a
  // rental are two transport resources, not two answers to one question, so a show
  // carrying both draws in BOTH places: a band in its vehicle lane and a band down
  // here. `deriveRentalLanes` groups a tour's arranged stops exactly the way
  // `deriveVehicleBands` does, so the two bands are the same shape and an edge
  // drag on either writes the same stop's dates. Derived here rather than in the
  // grid because the cell popover below reads the same bands.
  const rentalLanes = useMemo(() => {
    const tourNameById = new Map<string, string>();
    tours.forEach(t => tourNameById.set(t.id, t.name));
    return deriveRentalLanes(
      events.filter(e => e.transport_arranged && e.status !== 'cancelled'),
      tourNameById
    );
  }, [events, tours]);

  // Crew grouped per show, for the band hover card. Backend already orders by
  // name, so preserving arrival order keeps the card stable between hovers.
  const crewByEvent = useMemo(() => {
    const m = new Map<string, typeof crew>();
    crew.forEach(c => {
      const list = m.get(c.event_id);
      if (list) list.push(c); else m.set(c.event_id, [c]);
    });
    return m;
  }, [crew]);

  // Assignable vehicles for the rental band's "Assign vehicle (keeps transport)"
  // list (retired/OOC excluded), alphabetical by name to match the lane ordering.
  const usableVehicles = useMemo(
    () => vehicles
      .filter(v => !['ooc', 'retired'].includes(v.operational_status))
      .sort((a, b) => (a._extension?.name ?? a.display_code).localeCompare(b._extension?.name ?? b.display_code)),
    [vehicles]
  );

  // Popover + leg editor state
  const [popover, setPopover] = useState<{
    open: boolean;
    vehicleId: string;
    dateStr: string;
    position: { top: number; left: number };
  }>({ open: false, vehicleId: '', dateStr: '', position: { top: 0, left: 0 } });
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  // Detail modal for a Shipping / Rentals band (no assignment → keyed by event).
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [bandMenu, setBandMenu] = useState<BandContextMenuArgs | null>(null);
  const [hover, setHover] = useState<HoverCardState>(null);
  const hoverClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calendar-style hover card. A short debounced clear keeps it steady while the
  // pointer crosses between a tour band's segments (which fire leave→enter).
  //
  // Transport goes on the card as CHIPS, not as `_isRental`. That flag REPLACES the
  // status pill (EventHoverCard getSpecial), so a show with a truck and a drive
  // rental showed an orange "rental" pill and no real status. The chips say both
  // things without spending the status slot, and "driving" comes from an assigned
  // vehicle rather than from transport_mode.
  const handleBandHover = useCallback((event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => {
    if (hoverClearRef.current) clearTimeout(hoverClearRef.current);
    setHover({
      event,
      x, y, boxTop: box?.top, boxBottom: box?.bottom,
      crew: crewByEvent.get(event.id),
      transport: {
        hasVehicle: assignments.some(a => a.event_id === event.id),
        transportArranged: !!event.transport_arranged,
        transportMode: event.transport_mode,
      },
    });
  }, [crewByEvent, assignments]);
  const handleBandHoverEnd = useCallback(() => {
    if (hoverClearRef.current) clearTimeout(hoverClearRef.current);
    hoverClearRef.current = setTimeout(() => setHover(null), 60);
  }, []);

  const handleCellClick = useCallback((vehicleId: string, dateStr: string, rect: DOMRect) => {
    const pos = {
      top: Math.min(rect.bottom + 4, window.innerHeight - 320),
      left: Math.min(rect.left, window.innerWidth - 300),
    };
    setPopover({ open: true, vehicleId, dateStr, position: pos });
  }, []);

  const handleBandClick = useCallback((assignmentId: string) => {
    setEditingAssignmentId(assignmentId);
  }, []);

  // Drag a Shows-row badge onto a vehicle lane → assign (event date unchanged).
  const handleAssignDrop = useCallback(async (vehicleId: string, eventId: string) => {
    const res = await assignVehicle(eventId, vehicleId);
    if (!res.ok) toast.error(res.error || 'Failed to assign vehicle');
  }, [assignVehicle, toast]);

  // Drag a BAND onto a different vehicle row → move it there. A tour band moves as
  // a unit and the server resolves which shows that means, so the toast reports the
  // count it actually moved rather than what the visible window suggested.
  const handleBandMoveDrop = useCallback(async (toVehicleId: string, payload: BandMovePayload) => {
    const to = vehicles.find(v => v.id === toVehicleId);
    const toName = to?._extension?.name ?? to?.display_code ?? 'vehicle';
    const res = await moveAssignment(payload, toVehicleId);
    if (!res.ok) { toast.error(res.error || 'Failed to move'); return; }
    toast.success(
      res.scope === 'tour' && (res.moved ?? 0) > 1
        ? `Moved ${res.moved} shows to ${toName}`
        : `Moved to ${toName}`
    );
  }, [moveAssignment, vehicles, toast]);

  // Drag a Shows-row badge into the Shipping / Rentals lanes → mark transport
  // arranged. The band's flavor is the event's transport_mode, so it lands as a
  // "ship out" band from the untouched default, but as a DRIVE RENTAL when reached
  // from a vehicle band's "Also add shipping / rentals" (assigning that truck
  // already flipped the mode to driving). Both are the right default for their
  // path: adding external transport to a trucked show is usually a second, rented
  // truck. "Switch to shipping / drive rental" on the band's menu overrides it.
  const handleArrangeTransport = useCallback(async (eventId: string) => {
    const res = await setTransportArranged(eventId, true);
    if (res.ok) toast.success('Transport arranged');
    else toast.error(res.error || 'Failed to arrange transport');
  }, [setTransportArranged, toast]);

  const handleUnarrangeTransport = useCallback(async (eventId: string) => {
    const res = await setTransportArranged(eventId, false);
    if (res.ok) toast.success('Moved back to unsorted');
    else toast.error(res.error || 'Failed to update transport');
  }, [setTransportArranged, toast]);

  // Switch a bottom-lane band's flavor (ship-out legs vs drive-rental legs).
  // The flavor is a property of the BAND. A tour's arranged stops draw as one band
  // that takes its color and vocabulary from the first stop, so switching one
  // middle stop would toast success and change nothing visible (and re-open the
  // menu offering the reverse). Apply it to every arranged stop of the tour.
  const handleSetMode = useCallback(async (eventId: string, mode: 'shipping' | 'driving') => {
    const ev = eventById.get(eventId);
    const targets = ev?.tour_id
      ? events.filter(e => e.tour_id === ev.tour_id && e.transport_arranged && e.status !== 'cancelled').map(e => e.id)
      : [eventId];
    const results = await Promise.all(targets.map(id => setTransportMode(id, mode)));
    const failed = results.find(r => !r.ok);
    if (!failed) toast.success(mode === 'shipping' ? 'Switched to shipping' : 'Switched to drive rental');
    else toast.error(failed.error || 'Failed to switch transport');
  }, [setTransportMode, toast, eventById, events]);

  // Cross-section moves (from the band context menu).
  // Vehicle lane → Shipping / Rentals: drop the truck, mark transport arranged.
  const handleMoveToTransport = useCallback(async (eventId: string, vehicleId: string) => {
    const r1 = await unassignVehicle(eventId, vehicleId);
    if (!r1.ok) { toast.error(r1.error || 'Failed to move'); return; }
    const r2 = await setTransportArranged(eventId, true);
    if (r2.ok) toast.success('Moved to shipping / rentals');
    else toast.error(r2.error || 'Failed to move');
  }, [unassignVehicle, setTransportArranged, toast]);

  // Shipping / Rentals → ALSO on a truck. Additive on purpose: a truck and a rental
  // are two transport resources on one show (a full truck plus a rented box truck
  // for the overflow is the common shape), so this leaves transport_arranged alone
  // and the show draws a band in both sections. A swap is this plus "Remove from
  // transport", which sits right under it in the same menu.
  //
  // detectTour stays OFF: the show already has transport drawn somewhere, so this is
  // not the first-touch gesture the tour proposal is built to react to.
  const handleAddVehicle = useCallback(async (eventId: string, vehicleId: string) => {
    const res = await assignVehicle(eventId, vehicleId, { detectTour: false });
    if (res.ok) toast.success('Vehicle assigned');
    else toast.error(res.error || 'Failed to assign vehicle');
  }, [assignVehicle, toast]);

  // Unlink a tour at a boundary show (atomic split on the backend).
  const handleSplitTour = useCallback(async (tourId: string, eventId: string, side: 'left' | 'right') => {
    const res = await splitTour(tourId, eventId, side);
    if (res.ok) toast.success(side === 'left' ? 'Unlinked to the left' : 'Unlinked to the right');
    else toast.error(res.error || 'Failed to unlink');
  }, [splitTour, toast]);

  const handleCreateTourFromStacked = useCallback(async (eventIds: string[]) => {
    const res = await createTourFromEvents(eventIds);
    if (res.ok) toast.success('Tour created from stacked shows');
    else toast.error(res.error || 'Failed to create tour');
  }, [createTourFromEvents, toast]);

  // Right-click a band → open the context menu.
  const handleBandContextMenu = useCallback((args: BandContextMenuArgs) => {
    setBandMenu(args);
  }, []);

  const handleBandUnassign = useCallback(async (eventId: string, vehicleId: string) => {
    const res = await unassignVehicle(eventId, vehicleId);
    if (res.ok) toast.success('Vehicle unassigned');
    else toast.error(res.error || 'Failed to unassign vehicle');
  }, [unassignVehicle, toast]);

  // Escape closes popover/modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editingAssignmentId) setEditingAssignmentId(null);
      else if (editingEventId) setEditingEventId(null);
      else if (popover.open) setPopover(p => ({ ...p, open: false }));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [editingAssignmentId, editingEventId, popover.open]);

  const rangeLabel = `${dayjs.utc(windowStart).format('MMM D')} – ${dayjs.utc(windowEnd).format('MMM D, YYYY')}`;

  if (!canAccess) return <Redirect to="/" />;

  // Popover data for the clicked (vehicle, date).
  //
  // The Shipping / Rentals lanes come through here too, carrying
  // RENTAL_VEHICLE_ID as the vehicle. There is no `vehicles` row for that
  // sentinel, so every vehicle-shaped lookup below has to have a rental answer or
  // the picker opens empty and unable to assign.
  const popIsRental = popover.vehicleId === RENTAL_VEHICLE_ID;
  const popVehicle = vehicles.find(v => v.id === popover.vehicleId);
  const popVehicleName = popIsRental
    ? 'Shipping / Rentals'
    : (popVehicle?._extension?.name ?? popVehicle?.display_code ?? '');
  // Retired / OOC rigs are not assignable going forward (existing assignments
  // can still be removed — that's `canEdit`, not `canAssign`). A rental lane has
  // no operational status to disqualify it.
  const popCanAssign = popIsRental
    ? canEdit
    : (canEdit && !!popVehicle && !['ooc', 'retired'].includes(popVehicle.operational_status));
  // Shows offered for assignment. Every show on the day, in both branches: a show
  // with a truck can also take shipping / a rental (and the reverse), so filtering
  // either out would hide the exact combination producers asked for. The old rental
  // filter existed because the bottom lanes excluded vehicle-assigned shows, which
  // made the click set the flag, toast success and draw nothing; the lanes no longer
  // exclude them, so the band now appears where the click promised.
  const popEventsOnDay = eventsPerDay.get(popover.dateStr) || [];

  // Already-covered shows whose BAND covers the day.
  //
  // The rental branch reads from ALL events, not from popEventsOnDay: that map is
  // keyed on the SHOW SPAN while bands are drawn over bandExtent (ship_out through
  // ship_back). On a travel day the event is outside the show span, so filtering
  // popEventsOnDay left the popover saying "No events on this day" while a band sat
  // right under the cursor, and the show could not be un-arranged from there. The
  // vehicle branch never had this because it iterates `assignments`.
  const popAssignedEvents = !popover.open
    ? []
    : popIsRental
      // The lane bands themselves, not per-event extents: a tour band runs from
      // stop 1's ship-out to stop N's ship-back, and a travel day between two
      // stops sits inside the band but outside either stop's own window.
      ? rentalLanes.flat()
          .filter(b => b.baseLeave <= popover.dateStr && b.baseReturn >= popover.dateStr)
          .flatMap(b => b.events)
      : assignments
          .filter(a => a.vehicle_id === popover.vehicleId)
          .map(a => eventById.get(a.event_id))
          .filter((e): e is NonNullable<typeof e> => {
            if (!e) return false;
            const { start, end } = bandExtent(e);
            return start <= popover.dateStr && end >= popover.dateStr;
          });
  const popBlockRow = unavailability.find(
    u => u.asset_id === popover.vehicleId && u.start_date <= popover.dateStr && u.end_date >= popover.dateStr
  );
  const popBlock = popBlockRow
    ? { id: popBlockRow.id, notes: popBlockRow.notes || '', start: popBlockRow.start_date, end: popBlockRow.end_date }
    : null;

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary day:text-amber-700">Transport Scheduler</h1>
          <p className="text-xs text-on-surface-variant day:text-gray-500 mt-0.5">
            {canEdit
              ? 'Trucks, rentals & shipping for every show — drag a band edge to set the ship-out / return date'
              : 'View-only — transport and tours across the next 90 days'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DayModeToggle className="mr-1" />
          <Tooltip label="Earlier">
          <IconButton
            label="Earlier"
            onClick={() => { setFocusStart(null); setOffset(o => o - 1); }}
          >
            <IconChevronLeft />
          </IconButton>
          </Tooltip>
          <div className="text-sm text-on-surface day:text-gray-700 min-w-[200px] text-center">{rangeLabel}</div>
          <Tooltip label="Later">
          <IconButton
            label="Later"
            onClick={() => { setFocusStart(null); setOffset(o => o + 1); }}
          >
            <IconChevronRight />
          </IconButton>
          </Tooltip>
          <Appear show={offset !== 0 || !!focusStart} as="div">
            <button
              onClick={() => { setFocusStart(null); setOffset(0); }}
              className={buttonClass('quiet')}
            >
              Today
            </button>
          </Appear>
        </div>
      </div>

      <Reveal show={!!error}>
        <div className="bg-red-500/10 day:bg-red-50 border border-red-500/30 day:border-red-300 rounded-lg px-4 py-3 text-sm text-red-400 day:text-red-700">
          {error}
        </div>
      </Reveal>

      {loading && <LogoSpinner label="Loading the transport schedule" />}

      {!loading && (
        <FadeIn>
        <VehicleScheduleGrid
          scrollRef={scrollRef}
          days={days}
          todayIdx={todayIdx}
          canEdit={canEdit}
          onCellClick={handleCellClick}
          onBandClick={handleBandClick}
          onBandContextMenu={handleBandContextMenu}
          onAssignDrop={handleAssignDrop}
          onBandMoveDrop={handleBandMoveDrop}
          onRentalDrop={handleArrangeTransport}
          onRentalBandClick={(eventId) => setEditingEventId(eventId)}
          rentalLanes={rentalLanes}
          onBandHover={handleBandHover}
          onBandHoverEnd={handleBandHoverEnd}
        />
        </FadeIn>
      )}
      <EventHoverCardPortal hover={hover} />
      {!loading && canEdit && <VehicleDragController />}

      {/* Cell popover */}
      {popover.open && (
        <VehicleCellPopover
          key={`${popover.vehicleId}-${popover.dateStr}`}
          isOpen={popover.open}
          onClose={() => setPopover(p => ({ ...p, open: false }))}
          vehicleId={popover.vehicleId}
          vehicleName={popVehicleName}
          dateStr={popover.dateStr}
          canEdit={canEdit}
          canAssign={popCanAssign}
          eventsOnDay={popEventsOnDay}
          assignedEvents={popAssignedEvents}
          block={popBlock}
          position={popover.position}
        />
      )}

      {/* Leg editor */}
      {editingAssignmentId && (
        <LegEditorModal assignmentId={editingAssignmentId} canEdit={canEdit} onClose={() => setEditingAssignmentId(null)} />
      )}
      {editingEventId && (
        <LegEditorModal eventId={editingEventId} canEdit={canEdit} onClose={() => setEditingEventId(null)} />
      )}

      {/* Auto-tour confirmation (create / extend / dissolve) */}
      <TourProposalModal />

      {/* Right-click band menu (vehicle assignment or rental) */}
      {bandMenu && (
        <BandContextMenu
          x={bandMenu.x}
          y={bandMenu.y}
          title={bandMenu.eventName}
          items={
            bandMenu.kind === 'rental'
              ? [
                  { label: 'View event…', onClick: () => { history.push(`/events/${bandMenu.eventId}`); setBandMenu(null); } },
                  (eventById.get(bandMenu.eventId)?.transport_mode === 'shipping'
                    ? { label: 'Switch to drive rental', onClick: () => { handleSetMode(bandMenu.eventId, 'driving'); setBandMenu(null); } }
                    : { label: 'Switch to shipping', onClick: () => { handleSetMode(bandMenu.eventId, 'shipping'); setBandMenu(null); } }),
                  // ADDITIVE, not a move: assigning a truck here leaves the
                  // shipping / rental in place, so the show ends up with a band in
                  // both sections. A swap is this plus "Remove from transport"
                  // directly below — two clicks in one menu, which is why there is
                  // no second copy of the whole vehicle list for the move verb.
                  // (This is already the tallest menu in the app; 2N vehicle rows
                  // is how it stops fitting on a laptop.)
                  //
                  // Trucks already on the show are filtered out — offering one
                  // again is a no-op the backend swallows.
                  ...(() => {
                    const onShow = new Set(
                      assignments.filter(a => a.event_id === bandMenu.eventId).map(a => a.vehicle_id)
                    );
                    const addable = usableVehicles.filter(v => !onShow.has(v.id));
                    if (!addable.length) return [] as MenuItem[];
                    return [
                      { label: 'Assign vehicle (keeps transport)', header: true },
                      ...addable.map(v => ({
                        label: v._extension?.name ?? v.display_code,
                        onClick: () => { handleAddVehicle(bandMenu.eventId, v.id); setBandMenu(null); },
                      })),
                    ] as MenuItem[];
                  })(),
                  { label: 'Remove from transport', danger: true, onClick: () => { handleUnarrangeTransport(bandMenu.eventId); setBandMenu(null); } },
                ]
              : [
                  { label: 'View event…', onClick: () => { history.push(`/events/${bandMenu.eventId}`); setBandMenu(null); } },
                  { label: 'Edit drive legs…', onClick: () => { setEditingAssignmentId(bandMenu.assignmentId); setBandMenu(null); } },
                  // Add (keep the truck) vs move (drop it) — both offered while the
                  // show has no external transport. Once it does, neither applies:
                  // adding again is a no-op, and moving is exactly "Unassign
                  // vehicle" at the bottom of this menu.
                  ...(eventById.get(bandMenu.eventId)?.transport_arranged
                    ? []
                    : [
                        { label: 'Also add shipping / rentals', onClick: () => { handleArrangeTransport(bandMenu.eventId); setBandMenu(null); } },
                        { label: 'Move to shipping / rentals', onClick: () => { handleMoveToTransport(bandMenu.eventId, bandMenu.vehicleId); setBandMenu(null); } },
                      ] as MenuItem[]),
                  // Tour unlink — only when this show is in a tour and has a neighbor
                  // on that side (the boundary stays on the staying side).
                  ...(() => {
                    const ev = eventById.get(bandMenu.eventId);
                    const tourId = ev?.tour_id;
                    if (!ev || !tourId) return [];
                    const sibs = events
                      .filter(s => s.tour_id === tourId)
                      .sort((a, b) => (a.event_date < b.event_date ? -1 : a.event_date > b.event_date ? 1 : (a.id < b.id ? -1 : 1)));
                    const i = sibs.findIndex(s => s.id === ev.id);
                    const out: MenuItem[] = [];
                    if (i > 0) out.push({ label: 'Unlink ← left', onClick: () => { handleSplitTour(tourId, ev.id, 'left'); setBandMenu(null); } });
                    if (i >= 0 && i < sibs.length - 1) out.push({ label: 'Unlink right →', onClick: () => { handleSplitTour(tourId, ev.id, 'right'); setBandMenu(null); } });
                    if (out.length) out.unshift({ label: 'Tour', header: true });
                    return out;
                  })(),
                  // Right-clicked standalone show → link it to every standalone show
                  // STACKED with it (ship windows overlap = they share stacked days)
                  // on this vehicle, grouping them into a tour.
                  ...(() => {
                    const ev = eventById.get(bandMenu.eventId);
                    if (!ev || ev.tour_id) return [] as MenuItem[];
                    const ids = new Set(assignments.filter(a => a.vehicle_id === bandMenu.vehicleId).map(a => a.event_id));
                    const pool = events.filter(e => ids.has(e.id) && !e.tour_id && e.status !== 'cancelled');
                    const cluster = stackedWith(ev, pool);
                    if (cluster.length < 2) return [] as MenuItem[];
                    return [{ label: `Create tour from ${cluster.length} stacked shows`, onClick: () => { handleCreateTourFromStacked(cluster.map(e => e.id)); setBandMenu(null); } }] as MenuItem[];
                  })(),
                  { label: 'Unassign vehicle', danger: true, onClick: () => { handleBandUnassign(bandMenu.eventId, bandMenu.vehicleId); setBandMenu(null); } },
                ]
          }
          onClose={() => setBandMenu(null)}
        />
      )}

    </div>
  );
};

export default VehicleSchedulePage;
